import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Filter,
  Download,
  Upload,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  MapPin,
  Calendar,
  DollarSign,
  Activity,
  AlertTriangle,
  FileText,
  Building,
  TrendingUp,
} from 'lucide-react';
import {
  Work,
  WorkFilters,
  WorkListResponse,
  WorkStage,
  GrantLevel,
  CategoryPST,
  DEFAULT_FILTERS,
  PAGE_SIZES,
} from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';

// Safe number parsing function
const safeParseFloat = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
};

// Enhanced utility functions
const formatCurrency = (amount: number | string): string => {
  const numAmount = safeParseFloat(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numAmount);
};

const formatDate = (date: string | Date, format: 'short' | 'long' = 'short'): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (format === 'long') {
    return dateObj.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  return dateObj.toLocaleDateString('en-IN');
};

const calculateProgress = (work: Work): { physical: number; financial: number; overall: number } => {
  let physical = 0;
  let financial = 0;
  const lengthSanctioned = safeParseFloat(work.length_sanctioned_km);
  const lengthCompleted = safeParseFloat(work.cumulative_length_completed);
  if (lengthSanctioned > 0 && lengthCompleted) {
    physical = Math.round((lengthCompleted / lengthSanctioned) * 100);
  }
  const rasAmount = safeParseFloat(work.ras_amount);
  const estimateCost = safeParseFloat(work.estimate_cost);
  const cumExp = safeParseFloat(work.cum_exp_on_work);
  const totalSanctioned = rasAmount || estimateCost;
  if (totalSanctioned > 0) {
    financial = Math.round((cumExp / totalSanctioned) * 100);
  }
  const overall = lengthSanctioned > 0 ? Math.round((physical + financial) / 2) : financial;
  return {
    physical: Math.min(physical, 100),
    financial: Math.min(financial, 100),
    overall: Math.min(overall, 100),
  };
};

const getStageColor = (stage: WorkStage): { bg: string; text: string; border: string } => {
  const colors = {
    [WorkStage.NOT_STARTED]: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-300',
    },
    [WorkStage.IN_PROGRESS]: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-300',
    },
    [WorkStage.COMPLETED]: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-300',
    },
    [WorkStage.ON_HOLD]: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-300',
    },
    [WorkStage.PLANNING]: {
      bg: 'bg-red-100',
      text: 'text-orange-800',
      border: 'border-orange-300',
    },
  };
  return colors[stage] || colors[WorkStage.NOT_STARTED];
};

const isWorkDelayed = (work: Work): boolean => {
  if (!work.completion_date_set || work.abstract_stage_of_work === WorkStage.COMPLETED) {
    return false;
  }
  return new Date(work.completion_date_set) < new Date();
};

// Custom debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};

// FilterPanel Component
interface FilterPanelProps {
  filters: WorkFilters;
  onFiltersChange: (filters: WorkFilters) => void;
  onReset: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  onReset,
  isExpanded,
  onToggle,
}) => {
  const debouncedSearchTerm = useDebounce(filters.searchTerm || '', 300);

  useEffect(() => {
    if (debouncedSearchTerm !== filters.searchTerm) {
      onFiltersChange({ ...filters, searchTerm: debouncedSearchTerm, page: 1 });
    }
  }, [debouncedSearchTerm, filters, onFiltersChange]);

  const activeFiltersCount = useMemo(() => {
    const excludedKeys = ['page', 'limit', 'sortBy', 'sortOrder'];
    return Object.keys(filters).filter(
      (key) =>
        filters[key as keyof WorkFilters] !== undefined &&
        filters[key as keyof WorkFilters] !== null &&
        (typeof filters[key as keyof WorkFilters] === 'string'
          ? filters[key as keyof WorkFilters] !== ''
          : true) &&
        !excludedKeys.includes(key),
    ).length;
  }, [filters]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2 text-blue-600" />
            Filters & Search
          </h3>
          <div className="flex items-center space-x-4">
            {activeFiltersCount > 0 && (
              <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
            <button
              onClick={onToggle}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {isExpanded ? 'Collapse' : 'Expand Filters'}
            </button>
          </div>
        </div>
      </div>
      <div className="px-6 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by work name, agency, proceedings number..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={filters.searchTerm || ''}
            onChange={(e) => onFiltersChange({ ...filters, searchTerm: e.target.value })}
          />
        </div>
      </div>
      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grant Level</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.grantLevel || ''}
                onChange={(e) =>
                  onFiltersChange({ ...filters, grantLevel: e.target.value as GrantLevel || undefined })
                }
              >
                <option value="">All Grant Levels</option>
                {Object.values(GrantLevel).map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Stage</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.stage || ''}
                onChange={(e) => onFiltersChange({ ...filters, stage: e.target.value as WorkStage || undefined })}
              >
                <option value="">All Stages</option>
                {Object.values(WorkStage).map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.category || ''}
                onChange={(e) =>
                  onFiltersChange({ ...filters, category: e.target.value as CategoryPST || undefined })
                }
              >
                <option value="">All Categories</option>
                {Object.values(CategoryPST).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'P' ? 'Plain' : cat === 'S' ? 'SCP' : 'TSP'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Agency Name</label>
              <input
                type="text"
                placeholder="Agency name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.agencyName || ''}
                onChange={(e) => onFiltersChange({ ...filters, agencyName: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.fromDate ? new Date(filters.fromDate).toISOString().split('T')[0] : ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    fromDate: e.target.value ? new Date(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.toDate ? new Date(filters.toDate).toISOString().split('T')[0] : ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    toDate: e.target.value ? new Date(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Cost (₹)</label>
              <input
                type="number"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.minCost || ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    minCost: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Cost (₹)</label>
              <input
                type="number"
                placeholder="999999999"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.maxCost || ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    maxCost: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <button
              onClick={onReset}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Reset All Filters
            </button>
            <div className="text-sm text-gray-500">{activeFiltersCount} active filters</div>
          </div>
        </div>
      )}
    </div>
  );
};

// WorksTable Component
interface WorksTableProps {
  works: Work[];
  loading: boolean;
  onView: (work: Work) => void;
  onEdit: (work: Work) => void;
  onDelete: (work: Work) => void;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (field: string) => void;
}

const WorksTable: React.FC<WorksTableProps> = ({
  works,
  loading,
  onView,
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
}) => {
  const [selectedWorks, setSelectedWorks] = useState<number[]>([]);
  const [showActions, setShowActions] = useState<number | null>(null);

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 opacity-40" />;
    return sortOrder === 'ASC' ? (
      <ArrowUpDown className="w-4 h-4 text-blue-600" />
    ) : (
      <ArrowUpDown className="w-4 h-4 text-blue-600 rotate-180" />
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedWorks(checked ? works.map((w) => w.work_id) : []);
  };

  const handleSelectWork = (workId: number, checked: boolean) => {
    if (checked) {
      setSelectedWorks([...selectedWorks, workId]);
    } else {
      setSelectedWorks(selectedWorks.filter((id) => id !== workId));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading works...</p>
        </div>
      </div>
    );
  }

  if (works.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Works Found</h3>
          <p className="text-gray-600 mb-6">No works match your current filters. Try adjusting your search criteria.</p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4 mr-2 inline" />
            Add New Work
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {selectedWorks.length > 0 && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedWorks.length} work{selectedWorks.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                Bulk Edit
              </button>
              <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors">
                Bulk Delete
              </button>
              <button
                onClick={() => setSelectedWorks([])}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedWorks.length === works.length && works.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('sno')}
              >
                <div className="flex items-center space-x-1">
                  <span>SNO</span>
                  {getSortIcon('sno')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Work Details
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('estimate_cost')}
              >
                <div className="flex items-center space-x-1">
                  <span>Cost</span>
                  {getSortIcon('estimate_cost')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('abstract_stage_of_work')}
              >
                <div className="flex items-center space-x-1">
                  <span>Stage</span>
                  {getSortIcon('abstract_stage_of_work')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('oas_sanction_date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Sanction Date</span>
                  {getSortIcon('oas_sanction_date')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {works.map((work) => {
              const progress = calculateProgress(work);
              const delayed = isWorkDelayed(work);
              const stageColors = getStageColor(work.abstract_stage_of_work);
              return (
                <tr key={work.work_id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedWorks.includes(work.work_id)}
                      onChange={(e) => handleSelectWork(work.work_id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{work.sno}</span>
                      {delayed && <AlertTriangle className="w-4 h-4 text-red-500" />}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <p className="font-medium text-gray-900 truncate" title={work.work_name}>
                        {work.work_name}
                      </p>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center">
                          <Building className="w-3 h-3 mr-1" />
                          {work.agency_name}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            work.oas_grant_level === GrantLevel.CENTRAL
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {work.oas_grant_level}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900">{formatCurrency(work.estimate_cost)}</p>
                      {work.ras_amount && safeParseFloat(work.ras_amount) !== safeParseFloat(work.estimate_cost) && (
                        <p className="text-xs text-blue-600">RAS: {formatCurrency(work.ras_amount)}</p>
                      )}
                      <p className="text-xs text-gray-600">Spent: {formatCurrency(work.cum_exp_on_work)}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <p className="font-medium text-gray-900 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {work.district?.name || 'N/A'}
                      </p>
                      <p className="text-gray-600">{work.mandal?.name || 'N/A'}</p>
                      {work.length_sanctioned_km && safeParseFloat(work.length_sanctioned_km) > 0 && (
                        <p className="text-xs text-gray-500">{safeParseFloat(work.length_sanctioned_km).toFixed(1)} km</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Financial</span>
                        <span className="font-medium">{progress.financial}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress.financial}%` }}
                        ></div>
                      </div>
                      {work.length_sanctioned_km && safeParseFloat(work.length_sanctioned_km) > 0 && (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Physical</span>
                            <span className="font-medium">{progress.physical}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${progress.physical}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${stageColors.bg} ${stageColors.text}`}>
                        {work.abstract_stage_of_work}
                      </span>
                      {work.sub_stage_of_work && <p className="text-xs text-gray-600">{work.sub_stage_of_work}</p>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(work.oas_sanction_date)}
                    </div>
                    {work.completion_date_set && (
                      <div className={`text-xs mt-1 ${delayed ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        Due: {formatDate(work.completion_date_set)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="relative">
                      <button
                        onClick={() => setShowActions(showActions === work.work_id ? null : work.work_id)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {showActions === work.work_id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button
                            onClick={() => {
                              onView(work);
                              setShowActions(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </button>
                          <button
                            onClick={() => {
                              onEdit(work);
                              setShowActions(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Work
                          </button>
                          <button
                            onClick={() => {
                              onDelete(work);
                              setShowActions(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Work
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// WorksList Component
const WorksList: React.FC = () => {
  const [worksData, setWorksData] = useState<WorkListResponse>({
    works: [],
    total: 0,
    totalPages: 0,
    currentPage: 1,
    summary: {
      total_works: 0,
      total_estimate_cost: 0,
      total_ras_amount: 0,
      total_expenditure: 0,
      total_length_sanctioned: 0,
      total_length_completed: 0,
      total_bills_pending: 0,
      total_work_not_recorded: 0,
      financial_progress_percentage: 0,
      physical_progress_percentage: 0,
      avg_estimate_cost: 0,
      avg_completion_percentage: 0,
    },
    filters: DEFAULT_FILTERS,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);

  // Track filters separately to avoid infinite loops
  const [currentFilters, setCurrentFilters] = useState<WorkFilters>(DEFAULT_FILTERS);

  const fetchWorks = useCallback(async (filters: WorkFilters = DEFAULT_FILTERS) => {
    setLoading(true);
    try {
      const response = await worksApiService.getAllWorks(filters);
      setWorksData(response);
      setError(null);
    } catch (err) {
      setError('Failed to fetch works. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorks(currentFilters);
  }, [fetchWorks, currentFilters]);

  const handleFiltersChange = (newFilters: WorkFilters) => {
    const updatedFilters = { ...currentFilters, ...newFilters, page: 1 };
    setCurrentFilters(updatedFilters);
    setWorksData((prev) => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  const handleResetFilters = () => {
    setCurrentFilters(DEFAULT_FILTERS);
    setWorksData((prev) => ({ ...prev, filters: DEFAULT_FILTERS }));
  };

  const handleSort = (field: string) => {
    const updatedFilters = {
      ...currentFilters,
      sortBy: field,
      sortOrder: currentFilters.sortBy === field && currentFilters.sortOrder === 'ASC' ? 'DESC' : 'ASC' as 'ASC' | 'DESC',
    };
    setCurrentFilters(updatedFilters);
    setWorksData((prev) => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  const handlePageChange = (page: number) => {
    const updatedFilters = { ...currentFilters, page };
    setCurrentFilters(updatedFilters);
    setWorksData((prev) => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  const handlePageSizeChange = (limit: number) => {
    const updatedFilters = { ...currentFilters, limit, page: 1 };
    setCurrentFilters(updatedFilters);
    setWorksData((prev) => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  const handleView = (work: Work) => {
    console.log('View work:', work.work_id);
    // Implement view logic
  };

  const handleEdit = (work: Work) => {
    console.log('Edit work:', work.work_id);
    // Implement edit logic
  };

  const handleDelete = async (work: Work) => {
    if (window.confirm(`Are you sure you want to delete work ${work.sno}?`)) {
      setLoading(true);
      try {
        await worksApiService.deleteWork(work.work_id);
        await fetchWorks(currentFilters);
        setError(null);
      } catch (err) {
        setError('Failed to delete work. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Works Management</h1>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            Add New Work
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </button>
          <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Summary Section */}
      {worksData.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Filtered Works</p>
                <p className="text-2xl font-bold text-gray-900">{worksData.summary.total_works ?? 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sanctioned</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(worksData.summary.total_estimate_cost ?? 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spent</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(worksData.summary.total_expenditure ?? 0)}
                </p>
              </div>
              <Activity className="w-8 h-8 text-orange-500" />
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(worksData.summary.financial_progress_percentage ?? 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {(worksData.summary.financial_progress_percentage ?? 0).toFixed(1)}% utilization
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Physical Progress</p>
                <p className="text-lg font-bold text-gray-900">
                  {(worksData.summary.physical_progress_percentage ?? 0).toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(worksData.summary.physical_progress_percentage ?? 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {safeParseFloat(worksData.summary.total_length_completed).toFixed(1)} km completed
              </p>
            </div>
          </div>
        </div>
      )}

      <FilterPanel
        filters={currentFilters}
        onFiltersChange={handleFiltersChange}
        onReset={handleResetFilters}
        isExpanded={isFilterExpanded}
        onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
      />

      <WorksTable
        works={worksData.works}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        sortBy={currentFilters.sortBy || 'sno'}
        sortOrder={currentFilters.sortOrder || 'ASC'}
        onSort={handleSort}
      />

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={currentFilters.limit || PAGE_SIZES[0]}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-600">
            Showing {(worksData.currentPage - 1) * (currentFilters.limit || PAGE_SIZES[0]) + 1} to{' '}
            {Math.min(worksData.currentPage * (currentFilters.limit || PAGE_SIZES[0]), worksData.total)} of{' '}
            {worksData.total} works
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(worksData.currentPage - 1)}
            disabled={worksData.currentPage === 1}
            className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600">
            Page {worksData.currentPage} of {worksData.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(worksData.currentPage + 1)}
            disabled={worksData.currentPage === worksData.totalPages}
            className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorksList;